import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Card from './Card';
import Button from './Button';
import FormInput from './FormInput';
import Loading from './Loading';
import Alert from './Alert';
import { formatDate } from '../utils/helpers';

const TaskContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TaskItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.background.card};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border-left: 4px solid ${({ theme, $priority }) => 
    $priority === 'high' ? theme.colors.error.main :
    $priority === 'medium' ? theme.colors.warning.main :
    theme.colors.success.main
  };
  margin-bottom: 0.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const TaskContent = styled.div`
  flex: 1;
`;

const TaskTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: ${({ theme }) => theme.typography.sizes.md};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
`;

const TaskDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const TaskZone = styled.span`
  background-color: ${({ theme }) => theme.colors.primary.light};
  color: ${({ theme }) => theme.colors.primary.dark};
  padding: 0.2rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sizes.xs};
`;

const TaskDate = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const TaskActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const TaskStatus = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  background-color: ${({ theme, status }) => 
    status === 'completed' ? theme.colors.success.light :
    status === 'in-progress' ? theme.colors.warning.light :
    theme.colors.info.light
  };
  color: ${({ theme, status }) => 
    status === 'completed' ? theme.colors.success.dark :
    status === 'in-progress' ? theme.colors.warning.dark :
    theme.colors.info.dark
  };
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  flex: 1;
  min-width: 150px;
`;

const NoTasks = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const TaskForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

/**
 * TaskScheduler component for managing farm tasks
 * @param {Object} props
 * @param {Array} props.tasks - List of tasks
 * @param {Array} props.zones - List of farm zones
 * @param {Function} props.onTaskComplete - Function to call when a task is completed
 * @param {Function} props.onTaskDelete - Function to call when a task is deleted
 * @param {Function} props.onTaskAdd - Function to call when a task is added
 * @param {Function} props.onTaskEdit - Function to call when a task is edited
 */
const TaskScheduler = ({ 
  tasks = [], 
  zones = [], 
  onTaskComplete,
  onTaskDelete,
  onTaskAdd,
  onTaskEdit,
  loading = false,
  error = null
}) => {
  const [filteredTasks, setFilteredTasks] = useState(tasks);
  const [filters, setFilters] = useState({
    status: 'all',
    zone: 'all',
    priority: 'all'
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    zoneId: '',
    priority: 'medium',
    dueDate: new Date().toISOString().split('T')[0]
  });
  const [notification, setNotification] = useState(null);

  // Apply filters when tasks or filters change
  useEffect(() => {
    let result = [...tasks];
    
    if (filters.status !== 'all') {
      result = result.filter(task => task.status === filters.status);
    }
    
    if (filters.zone !== 'all') {
      result = result.filter(task => task.zoneId === filters.zone);
    }
    
    if (filters.priority !== 'all') {
      result = result.filter(task => task.priority === filters.priority);
    }
    
    setFilteredTasks(result);
  }, [tasks, filters]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle task completion
  const handleCompleteTask = (taskId) => {
    if (onTaskComplete) {
      onTaskComplete(taskId);
      setNotification({
        type: 'success',
        message: 'Task marked as completed!'
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  // Handle task deletion
  const handleDeleteTask = (taskId) => {
    if (onTaskDelete) {
      onTaskDelete(taskId);
      setNotification({
        type: 'info',
        message: 'Task deleted successfully'
      });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!newTask.title || !newTask.zoneId) {
      setNotification({
        type: 'error',
        message: 'Please fill in all required fields'
      });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    
    if (editingTask) {
      // Edit existing task
      if (onTaskEdit) {
        onTaskEdit(editingTask.id, newTask);
        setNotification({
          type: 'success',
          message: 'Task updated successfully'
        });
      }
      setEditingTask(null);
    } else {
      // Add new task
      if (onTaskAdd) {
        onTaskAdd(newTask);
        setNotification({
          type: 'success',
          message: 'New task added successfully'
        });
      }
    }
    
    // Reset form
    setNewTask({
      title: '',
      description: '',
      zoneId: '',
      priority: 'medium',
      dueDate: new Date().toISOString().split('T')[0]
    });
    setShowAddForm(false);
    setTimeout(() => setNotification(null), 3000);
  };

  // Handle edit task
  const handleEditTask = (task) => {
    setEditingTask(task);
    setNewTask({
      title: task.title,
      description: task.description || '',
      zoneId: task.zoneId,
      priority: task.priority,
      dueDate: new Date(task.dueDate).toISOString().split('T')[0]
    });
    setShowAddForm(true);
  };

  // Cancel form
  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingTask(null);
    setNewTask({
      title: '',
      description: '',
      zoneId: '',
      priority: 'medium',
      dueDate: new Date().toISOString().split('T')[0]
    });
  };

  // Find zone name by ID
  const getZoneName = (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    return zone ? zone.name : 'Unknown Zone';
  };

  return (
    <Card 
      title="Task Scheduler" 
      action={
        !showAddForm && (
          <Button 
            variant="primary" 
            size="sm" 
            onClick={() => setShowAddForm(true)}
          >
            Add Task
          </Button>
        )
      }
    >
      {notification && (
        <Alert 
          type={notification.type} 
          message={notification.message} 
          onClose={() => setNotification(null)}
          dismissible
        />
      )}
      
      <FilterContainer>
        <FilterGroup>
          <FormInput
            type="select"
            label="Status"
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'pending', label: 'Pending' },
              { value: 'in-progress', label: 'In Progress' },
              { value: 'completed', label: 'Completed' }
            ]}
          />
        </FilterGroup>
        <FilterGroup>
          <FormInput
            type="select"
            label="Zone"
            name="zone"
            value={filters.zone}
            onChange={handleFilterChange}
            options={[
              { value: 'all', label: 'All Zones' },
              ...zones.map(zone => ({ value: zone.id, label: zone.name }))
            ]}
          />
        </FilterGroup>
        <FilterGroup>
          <FormInput
            type="select"
            label="Priority"
            name="priority"
            value={filters.priority}
            onChange={handleFilterChange}
            options={[
              { value: 'all', label: 'All Priorities' },
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' }
            ]}
          />
        </FilterGroup>
      </FilterContainer>

      {showAddForm && (
        <TaskForm onSubmit={handleSubmit}>
          <FormInput
            label="Task Title"
            name="title"
            value={newTask.title}
            onChange={handleInputChange}
            placeholder="Enter task title"
            required
          />
          
          <FormInput
            type="textarea"
            label="Description"
            name="description"
            value={newTask.description}
            onChange={handleInputChange}
            placeholder="Enter task description"
            rows={3}
          />
          
          <FormRow>
            <FormInput
              type="select"
              label="Zone"
              name="zoneId"
              value={newTask.zoneId}
              onChange={handleInputChange}
              options={zones.map(zone => ({ value: zone.id, label: zone.name }))}
              required
            />
            
            <FormInput
              type="select"
              label="Priority"
              name="priority"
              value={newTask.priority}
              onChange={handleInputChange}
              options={[
                { value: 'high', label: 'High' },
                { value: 'medium', label: 'Medium' },
                { value: 'low', label: 'Low' }
              ]}
            />
            
            <FormInput
              type="date"
              label="Due Date"
              name="dueDate"
              value={newTask.dueDate}
              onChange={handleInputChange}
            />
          </FormRow>
          
          <FormActions>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleCancelForm}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingTask ? 'Update Task' : 'Add Task'}
            </Button>
          </FormActions>
        </TaskForm>
      )}

      {loading ? (
        <Loading text="Loading tasks..." />
      ) : error ? (
        <Alert type="error" message={error} />
      ) : filteredTasks.length === 0 ? (
        <NoTasks>
          <p>No tasks found matching your filters.</p>
        </NoTasks>
      ) : (
        <TaskContainer>
          {filteredTasks.map(task => (
            <TaskItem key={task.id} $priority={task.priority}>
              <TaskContent>
                <TaskTitle>
                  {task.title}
                </TaskTitle>
                <TaskDescription>{task.description}</TaskDescription>
                <TaskMeta>
                  <TaskZone>{getZoneName(task.zoneId)}</TaskZone>
                  <TaskDate>
                    Due: {formatDate(task.dueDate)}
                  </TaskDate>
                  <TaskStatus status={task.status}>
                    {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                  </TaskStatus>
                </TaskMeta>
              </TaskContent>
              <TaskActions>
                {task.status !== 'completed' && (
                  <Button 
                    variant="success" 
                    size="sm" 
                    onClick={() => handleCompleteTask(task.id)}
                  >
                    Complete
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditTask(task)}
                >
                  Edit
                </Button>
                <Button 
                  variant="danger" 
                  size="sm" 
                  onClick={() => handleDeleteTask(task.id)}
                >
                  Delete
                </Button>
              </TaskActions>
            </TaskItem>
          ))}
        </TaskContainer>
      )}
    </Card>
  );
};

export default TaskScheduler;
